import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n";

/**
 * DeTrust Logo — the shared brand mark.
 *
 * A network of agents: a central node with six spokes to satellites of
 * deliberately unequal size. The sizes are the point — your agent in the middle,
 * the agents it deals with around it, and they do not all carry the same weight.
 *
 * Geometry is authored in `mobile/tool/brand/gen_mark.py` on a 200x200 grid and
 * MIRRORED here, identical to the Flutter painter in
 * `mobile/lib/core/brand/detrust_mark.dart`. Never edit the path data by hand —
 * edit the generator, re-run it, and paste; it checks both mirrors against its
 * own output.
 *
 * Monochrome by design. Amber means "needs you" everywhere else in the system,
 * so it must not appear in the logo. (An earlier version of this file drew a
 * letter D with an amber segment; it was a monogram, it said nothing about what
 * the product does, and it spent the one colour that carries meaning.)
 */

export interface LogoProps {
  /**
   * Size of the logo
   * - sm: Small size for compact spaces (8px icon container)
   * - md: Medium size for headers (10px icon container) - default
   * - lg: Large size for hero sections (12px icon container)
   */
  size?: "sm" | "md" | "lg";

  /**
   * Whether to display brand text next to the icon
   * @default true
   */
  withText?: boolean;

  /**
   * Retained for call-site compatibility. All three render identically — the
   * mark is monochrome brand blue and takes no gradient.
   * @default 'gold'
   */
  variant?: "gold" | "emerald" | "gradient";

  /**
   * Click handler for navigation
   */
  onClick?: () => void;

  /**
   * Additional CSS classes
   */
  className?: string;
}

const sizeConfig = {
  sm: {
    container: "w-8 h-8",
    icon: "w-6 h-6",
    title: "text-base",
    subtitle: "text-[10px]",
  },
  md: {
    container: "w-10 h-10",
    icon: "w-8 h-8",
    title: "text-xl",
    subtitle: "text-xs",
  },
  lg: {
    container: "w-12 h-12",
    icon: "w-10 h-10",
    title: "text-2xl",
    subtitle: "text-sm",
  },
};

const variantConfig = {
  gold: "bg-transparent shadow-sm group-hover:shadow-md",
  emerald:
    "bg-transparent shadow-sm group-hover:shadow-md",
  gradient:
    "bg-transparent shadow-sm group-hover:shadow-md",
};

/**
 * Logo Component
 */
export function Logo({
  size = "md",
  withText = true,
  variant = "gold",
  onClick,
  className,
}: LogoProps) {
  const { t } = useTranslation();
  const sizes = sizeConfig[size];
  const variantStyles = variantConfig[variant];
  const isClickable = !!onClick;

  return (
    <div
      className={cn("flex items-center gap-3 group", className)}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      aria-label={t("brand.logoLabel")}
    >
      {/* Icon Container */}
      <div
        className={cn(
          sizes.container,
          "rounded-xl flex items-center justify-center transition-all group-hover:scale-110",
          variantStyles,
        )}
      >
        {/* The mark. Stroked paths only — spokes first, then the satellite
            rings, then the centre, so the rings sit clean where they meet. */}
        <svg
          viewBox="0 0 200 200"
          className={cn(sizes.icon)}
          aria-hidden="true"
          fill="none"
          stroke="rgb(var(--detrust-mark, 0 87 133))"
          strokeLinecap="round"
        >
          <path d="M120.60,108.15 L154.85,113.82" strokeWidth="2.80" />
          <path d="M111.12,86.09 L148.85,30.33" strokeWidth="2.80" />
          <path d="M76.40,105.19 L40.15,106.30" strokeWidth="2.80" />
          <path d="M105.30,125.75 L120.21,173.34" strokeWidth="2.80" />
          <path d="M88.99,84.47 L74.66,54.72" strokeWidth="2.80" />
          <path d="M82.22,119.53 L39.12,158.93" strokeWidth="2.80" />
          <path d="M157.53,116.80 a15.35,15.35 0 1,0 30.71,0 a15.35,15.35 0 1,0 -30.71,0" strokeWidth="2.99" />
          <path d="M144.89,17.36 a12.73,12.73 0 1,0 25.47,0 a12.73,12.73 0 1,0 -25.47,0" strokeWidth="2.99" />
          <path d="M11.76,106.77 a12.73,12.73 0 1,0 25.47,0 a12.73,12.73 0 1,0 -25.47,0" strokeWidth="2.99" />
          <path d="M114.18,185.53 a9.84,9.84 0 1,0 19.69,0 a9.84,9.84 0 1,0 -19.69,0" strokeWidth="2.99" />
          <path d="M62.25,45.09 a7.77,7.77 0 1,0 15.53,0 a7.77,7.77 0 1,0 -15.53,0" strokeWidth="2.99" />
          <path d="M23.77,166.02 a7.59,7.59 0 1,0 15.17,0 a7.59,7.59 0 1,0 -15.17,0" strokeWidth="2.99" />
          <path d="M79.32,104.52 a19.33,19.33 0 1,0 38.66,0 a19.33,19.33 0 1,0 -38.66,0" strokeWidth="3.25" />
        </svg>
      </div>

      {/* Brand Text */}
      {withText && (
        <div>
          <h1
            className={cn(
              sizes.title,
              "font-bold text-foreground leading-tight",
            )}
          >
            {t("brand.name")}
          </h1>
          <p
            className={cn(
              sizes.subtitle,
              "text-muted-foreground leading-tight whitespace-nowrap",
            )}
          >
            {t("brand.tagline")}
          </p>
        </div>
      )}
    </div>
  );
}
